/* global document */
import * as React from "react"
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react"
import { createPortal } from "react-dom"
import { applyReactStyle } from "@/utils/applyReactStyle"

import { useMapContext } from "@/components/maps/mapLibre/MapContext"
import { deepEqual } from "@/utils/deepEqual"
import { compareClassNames } from "@/utils/compareClassNames"
import { Popup as PopupInstance, PopupOptions } from "maplibre-gl"

export type PopupEvent = {
  type: "open" | "close"
  target: PopupInstance
}

export type PopupProps = PopupOptions & {
  /** Longitude of the anchor location */
  longitude: number
  /** Latitude of the anchor location */
  latitude: number

  /** CSS style override, applied to the control's container */
  style?: React.CSSProperties

  onOpen?: (e: PopupEvent) => void
  onClose?: (e: PopupEvent) => void
  children?: React.ReactNode
}

export const Popup = memo(
  forwardRef((props: PopupProps, ref: React.Ref<PopupInstance>) => {
    const map = useMapContext()?.getMapInstance()
    const container = useMemo(() => {
      return document.createElement("div")
    }, [])
    const thisRef = useRef({ props })

    const popup: PopupInstance = useMemo(() => {
      const options = { ...props }
      const pp = new PopupInstance(options)
      pp.setLngLat([props.longitude, props.latitude])
      pp.once("open", (e) => {
        thisRef.current.props.onOpen?.(e as PopupEvent)
      })
      return pp
    }, [])

    useEffect(() => {
      const onClose = (e: any) => {
        thisRef.current.props.onClose?.(e as PopupEvent)
      }
      popup.on("close", onClose)
      if (map) {
        popup.setDOMContent(container).addTo(map)
      }
      return () => {
        // https://github.com/visgl/react-map-gl/issues/1825
        // onClose should not be fired if the popup is removed by unmounting
        // When using React strict mode, the component is mounted twice.
        // Firing the onClose callback here would be a false signal to remove the component.
        popup.off("close", onClose)
        if (popup.isOpen()) {
          popup.remove()
        }
      }
    }, [map])

    useEffect(() => {
      if (props.style) {
        applyReactStyle(popup.getElement(), props.style)
      }
    }, [props.style])

    useImperativeHandle(ref, () => popup, [])

    if (popup.isOpen()) {
      const oldProps = thisRef.current.props
      if (
        popup.getLngLat().lng !== props.longitude ||
        popup.getLngLat().lat !== props.latitude
      ) {
        popup.setLngLat([props.longitude, props.latitude])
      }
      if (props.offset && !deepEqual(oldProps.offset, props.offset)) {
        popup.setOffset(props.offset)
      }
      if (
        oldProps.anchor !== props.anchor ||
        oldProps.maxWidth !== props.maxWidth
      ) {
        popup.options.anchor = props.anchor
        if (props.maxWidth) {
          popup.setMaxWidth(props.maxWidth)
        }
      }
      const classNameDiff = compareClassNames(
        oldProps.className,
        props.className,
      )
      if (classNameDiff) {
        for (const c of classNameDiff) {
          popup.toggleClassName(c)
        }
      }
      thisRef.current.props = props
    }

    return createPortal(props.children, container)
  }),
)
